import { DecodeText } from './system';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { AssessmentStatus, ReadinessBand, SubjectId } from '@/core/types';
import { catalog } from '@/data/catalog';
import { STATUS_LABEL } from '@/engine/estimates';
import { BAND_LABEL } from '@/engine/readiness';
import { useCountUp } from '@/app/hooks';
import { Icon, SUBJECT_GLYPH } from './Icon';
import { L } from '@/i18n';

export function Sys({ code, children, plain }: { code?: string; children: ReactNode; plain?: boolean }) {
  return (
    <span className={`sys${plain ? ' plain' : ''}`}>
      {code && <span className="code">{code}</span>}
      {children}
    </span>
  );
}

export function Bracket({ children, className = '', violet, style }: { children: ReactNode; className?: string; violet?: boolean; style?: CSSProperties }) {
  return (
    <div className={`bracket ${violet ? 'violet' : ''} ${className}`} style={style}>
      <span className="bk tl" /><span className="bk tr" /><span className="bk bl" /><span className="bk br" />
      {children}
    </div>
  );
}

export function Meter({ value, tone, thin, tall, seg, label }: { value: number; tone?: 'violet' | 'amber' | 'red'; thin?: boolean; tall?: boolean; seg?: boolean; label?: string }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className={`meter ${tone ?? ''} ${thin ? 'thin' : ''} ${tall ? 'tall' : ''} ${seg ? 'seg' : ''}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={label}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Ring({ value, size = 64, stroke = 5, color = 'var(--accent)', children, track = 'var(--track)' }: { value: number; size?: number; stroke?: number; color?: string; children?: ReactNode; track?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - v)}
          style={{ transition: 'stroke-dashoffset 1100ms cubic-bezier(.16,1,.3,1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>{children}</div>
    </div>
  );
}

export function StatusChip({ status, short }: { status: AssessmentStatus; short?: boolean }) {
  return (
    <span className={`status ${status}`} title={STATUS_LABEL[status]}>
      <i />
      {short ? STATUS_LABEL[status].split(' ')[0] : STATUS_LABEL[status]}
    </span>
  );
}

export function BandTag({ band, provisional }: { band: ReadinessBand; provisional?: boolean }) {
  return (
    <span className="row" style={{ gap: 6 }}>
      <span className={`band-tag ${band}`}>{BAND_LABEL[band]}</span>
      {provisional && band !== 'UNASSESSED' && <span className="chip" title={L('Based on limited evidence — will sharpen as you learn', 'Basert på lite informasjon — blir skarpere etter hvert som du lærer')}>{L('provisional', 'foreløpig')}</span>}
    </span>
  );
}

export function Metric({ value, label, suffix, decimals = 0, glow }: { value: number; label: string; suffix?: string; decimals?: number; glow?: boolean }) {
  const v = useCountUp(value);
  return (
    <div className="metric">
      <span className={`v ${glow ? 'glow-text' : ''}`}>
        {v.toFixed(decimals)}
        {suffix && <small>{suffix}</small>}
      </span>
      <span className="k">{label}</span>
    </div>
  );
}

export function Divider() {
  return <div className="divider" />;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && <div className="mt">{action}</div>}
    </div>
  );
}

export function SubjectBadge({ id, size = 34 }: { id: SubjectId | string; size?: number }) {
  const s = catalog.subject(id);
  const hue = s?.hue ?? 200;
  return (
    <span
      title={s?.name}
      style={{
        width: size, height: size, flex: 'none', display: 'grid', placeItems: 'center',
        color: `hsl(${hue} 85% var(--badge-l))`,
        background: `radial-gradient(circle at 30% 30%, hsla(${hue},95%,60%,0.28), hsla(${hue},95%,40%,0.06))`,
        border: `1px solid hsla(${hue},95%,65%,0.45)`,
        clipPath: 'polygon(25% 3%, 75% 3%, 100% 50%, 75% 97%, 25% 97%, 0 50%)',
      }}
    >
      <Icon name={SUBJECT_GLYPH[id] ?? 'hex'} size={size * 0.5} />
    </span>
  );
}

export function Provenance({ kind }: { kind: 'OFFICIAL' | 'STUDIO_DERIVED' | 'DEMO' | 'GENERATED' | 'AUTHORED_DEMO' | 'UNVERIFIED' }) {
  const map = {
    OFFICIAL: ['official', L('Official curriculum (Udir)', 'Offisiell læreplan (Udir)')],
    UNVERIFIED: ['official', L('Official ref · unverified', 'Offisiell ref. · ikke verifisert')],
    STUDIO_DERIVED: ['derived', L('Made by Studio', 'Laget av Studio')],
    GENERATED: ['derived', L('Generated', 'Generert')],
    DEMO: ['demo', L('Demo content', 'Demoinnhold')],
    AUTHORED_DEMO: ['demo', L('Demo item', 'Demooppgave')],
  } as const;
  const [cls, label] = map[kind];
  return <span className={`chip ${cls}`}>{label}</span>;
}

export function LevelHex({ level, size = 36 }: { level: number; size?: number }) {
  return (
    <div className="lvl-hex" style={{ width: size, height: size * 1.1 }}>
      <svg viewBox="0 0 40 44">
        <defs>
          <linearGradient id="hexg" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.9" />
            <stop offset="1" stopColor="var(--violet)" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        <path d="M20 2 37 11.5v21L20 42 3 32.5v-21Z" fill="var(--plate-solid)" stroke="url(#hexg)" strokeWidth="2" />
      </svg>
      <span>{level}</span>
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { id: T; label: string; count?: number }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((it) => (
        <button key={it.id} role="tab" aria-selected={value === it.id} className={`tab ${value === it.id ? 'on' : ''}`} onClick={() => onChange(it.id)}>
          {it.label}
          {it.count !== undefined && <span className="faint mono" style={{ marginLeft: 6, fontSize: 11 }}>{it.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Modal({ onClose, children, label }: { onClose: () => void; children: ReactNode; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal plate hi" role="dialog" aria-modal="true" aria-label={label} ref={ref}>
        {children}
      </div>
    </div>
  );
}

export function PageHead({ code, title, lede, actions }: { code: string; title: ReactNode; lede?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <Sys code={code}>{L('Studio', 'Studio')}</Sys>
        <h1 className="mt" style={{ marginTop: 8 }}>{typeof title === 'string' ? <DecodeText text={title} /> : title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="row wrap">{actions}</div>}
    </header>
  );
}
